import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Badge } from '@components/base/display';
import { Table } from '@components/base/data';
import { HStack, VStack } from '@components/base/layout';
import { Alert, Spinner } from '@components/base/feedback';
import { Select } from '@components/base/inputs';
import { useQuery } from '@tanstack/react-query';
import { workflowExecutionsService } from '../../api/workflowExecutionsService';
import { workflowDefinitionsService } from '../../api/workflowDefinitionsService';
import type { WorkflowExecution } from '../../api/workflowExecutionsService';
import type { WorkflowDefinition } from '../../api/workflowDefinitionsService';

const statusBadgeVariant: Record<
  string,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  pending: 'default',
  running: 'info',
  paused: 'warning',
  waiting_input: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

const statusFilterOptions = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'waiting_input', label: 'Waiting Input' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function formatDate(dateString: string | null): string {
  if (!dateString) return '\u2014';
  return new Date(dateString).toLocaleString();
}

function formatDuration(
  startedAt: string | null,
  completedAt: string | null,
  status: string,
): string {
  if (!startedAt) return '\u2014';

  const start = new Date(startedAt).getTime();
  const end =
    completedAt && status !== 'running'
      ? new Date(completedAt).getTime()
      : Date.now();
  const diffMs = Math.max(0, end - start);
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function WorkflowExecutionsListPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [definitionFilter, setDefinitionFilter] = useState('');

  const { data: definitionsData } = useQuery({
    queryKey: ['admin', 'workflows'],
    queryFn: () => workflowDefinitionsService.list(),
  });

  const definitions: WorkflowDefinition[] = definitionsData?.data ?? [];

  const definitionFilterOptions = [
    { value: '', label: 'All' },
    ...definitions.map((def) => ({ value: def.id, label: def.name })),
  ];

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'admin',
      'workflows',
      'executions',
      {
        status: statusFilter,
        workflow_definition_id: definitionFilter,
      },
    ],
    queryFn: () =>
      workflowExecutionsService.listWithFilters({
        status: statusFilter || undefined,
        workflow_definition_id: definitionFilter || undefined,
      }),
  });

  const items: WorkflowExecution[] = data?.data ?? [];

  // ---- Loading state ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-16">
        <Spinner size="lg" />
        <Text color="muted">Loading workflow executions...</Text>
      </VStack>
    );
  }

  // ---- Error state ----

  if (error) {
    return (
      <Alert variant="error" title="Failed to load workflow executions">
        {error instanceof Error
          ? error.message
          : 'An unexpected error occurred while loading workflow executions.'}
      </Alert>
    );
  }

  // ---- Main content ----

  return (
    <VStack gap={6}>
      {/* Page header */}
      <VStack gap={1}>
        <Text as="h1" size="2xl" weight="bold">
          Workflow Executions
        </Text>
        <Text size="sm" color="muted">
          Monitor and manage workflow execution runs
        </Text>
      </VStack>

      {/* Filters */}
      <HStack gap={4}>
        <Select
          label="Status"
          options={statusFilterOptions}
          value={statusFilter}
          onChange={(val) => setStatusFilter(val as string)}
          size="sm"
        />
        <Select
          label="Workflow"
          options={definitionFilterOptions}
          value={definitionFilter}
          onChange={(val) => setDefinitionFilter(val as string)}
          size="sm"
        />
      </HStack>

      {/* Table or empty state */}
      {items.length === 0 ? (
        <Alert variant="info" title="No workflow executions found">
          No workflow executions match the current filters.
        </Alert>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Workflow</Table.HeaderCell>
              <Table.HeaderCell align="center">Status</Table.HeaderCell>
              <Table.HeaderCell>Current Step</Table.HeaderCell>
              <Table.HeaderCell align="center">Steps</Table.HeaderCell>
              <Table.HeaderCell>Started</Table.HeaderCell>
              <Table.HeaderCell>Duration</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.map((execution) => (
              <Table.Row key={execution.id} onClick={() => navigate(`/admin/workflows/executions/${execution.id}`)}>
                <Table.Cell>
                  <VStack gap={0}>
                    <Text weight="medium">
                      {execution.definition?.name ?? '\u2014'}
                    </Text>
                    <Text size="sm" color="muted">
                      {execution.id.slice(0, 8)}
                    </Text>
                  </VStack>
                </Table.Cell>
                <Table.Cell align="center">
                  <Badge
                    variant={statusBadgeVariant[execution.status] ?? 'default'}
                    size="sm"
                  >
                    {execution.status.replace('_', ' ')}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm">
                    {execution.current_step?.name ?? '\u2014'}
                  </Text>
                </Table.Cell>
                <Table.Cell align="center">
                  <Text size="sm">{execution.step_executions_count ?? 0}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {formatDate(execution.started_at)}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {formatDuration(
                      execution.started_at,
                      execution.completed_at,
                      execution.status,
                    )}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </VStack>
  );
}
